import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { getPolicyPremiumTotal } from "../utils/policyCalc";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "../components/ui/sheet";
import { Plus, Pencil, Trash2, Search, FileText, User, Building2, Filter, SlidersHorizontal, X, CheckCircle, Download, AlertCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import NameReviewDialog from "../components/NameReviewDialog";
import VoiceInput from "../components/VoiceInput";
import DocumentsDialog from "../components/DocumentsDialog";
import DocumentDropzone from "../components/DocumentDropzone";
import DataPagination from "../components/DataPagination";
import { ExportColumnsDialog } from "../components/ExportColumnsDialog";
import { SortableHeader, sortData } from "../components/SortableHeader";
import { Badge } from "../components/ui/badge";
import { Skeleton, TableSkeleton } from "../components/ui/skeleton";
import { ConfirmDialog, DeleteConfirmDialog } from "../components/ui/confirm-dialog";
import { validators } from "../lib/validationSchemas";

// Database CAP italiani (esempi principali città)
// Municipalities loaded from API
let _municipalitiesCache = null;

export default function ClientsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distingue "non c'e' nessun cliente" da "il caricamento e' fallito":
  // senza questo, un errore di rete mostrava "Crea il primo!" come se
  // l'archivio fosse vuoto.
  const [loadError, setLoadError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedClientForDocs, setSelectedClientForDocs] = useState(null);
  const [showOCR, setShowOCR] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [parsingCF, setParsingCF] = useState(false);
  const [municipalities, setMunicipalities] = useState({});
  const pendingFileRef = useRef(null);
  
  // Load municipalities data once
  useEffect(() => {
    const loadMunicipalities = async () => {
      if (_municipalitiesCache) {
        setMunicipalities(_municipalitiesCache);
        return;
      }
      try {
        const res = await axios.get("/api/clients/municipalities");
        _municipalitiesCache = res.data;
        setMunicipalities(res.data);
      } catch (e) {
        // Fallback silently
      }
    };
    loadMunicipalities();
  }, []);
  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // La rimozione dell'alias email cancellava subito, senza chiedere nulla.
  const [removeAliasOpen, setRemoveAliasOpen] = useState(false);
  const [removingAlias, setRemovingAlias] = useState(false);
  
  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const ITEMS_PER_PAGE = 25;
  
  // Form validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Advanced search filters
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tableSort, setTableSort] = useState({ key: "name", dir: "asc" });
  const [advancedFilters, setAdvancedFilters] = useState({
    clientType: "", // private, business
    city: "",
    province: "",
    hasPolicies: "", // yes, no, all
    fiscalCode: "",
    email: "",
    phone: "",
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  
  const [formData, setFormData] = useState({
    client_type: "private",
    name: "",
    first_name: "",
    last_name: "",
    name_split_source: null,
    email: "",
    phone: "",
    fiscal_code: "",
    birth_date: "",
    birth_place: "",
    birth_place_province: "",
    gender: "",
    pec: "",
    sdi_code: "",
    referent: "",
    address: "",
    city: "",
    cap: "",
    province: "",
    notes: "",
    create_alias: false,
  });

  // Validate a single field
  const validateField = (name, value) => {
    let error = null;
    
    switch (name) {
      case "name":
        if (!value || value.trim().length < 2) {
          error = "Il nome deve avere almeno 2 caratteri";
        }
        break;
      case "email":
        if (value && !validators.email(value)) {
          error = "Email non valida";
        }
        break;
      case "phone":
        if (value && !validators.phone(value)) {
          error = "Numero di telefono non valido";
        }
        break;
      case "fiscal_code":
        if (formData.client_type === "private" && value) {
          if (!validators.fiscalCode(value)) {
            error = "Codice fiscale non valido (16 caratteri)";
          }
        } else if (formData.client_type === "business" && value) {
          if (!validators.vatNumber(value)) {
            error = "Partita IVA non valida (11 cifre)";
          }
        }
        break;
      case "cap":
        if (value && !validators.postalCode(value)) {
          error = "CAP non valido (5 cifre)";
        }
        break;
      case "province":
        if (value && !validators.province(value)) {
          error = "Provincia non valida (2 lettere)";
        }
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [name]: error
    }));
    
    return !error;
  };

  // Handle field blur for validation
  const handleFieldBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, formData[name]);
  };

  // v3.10: l'etichetta `name` (quella che si vede in elenco) si ricostruisce da
  // cognome + nome SOLO quando entrambi ci sono. Altrimenti si conserva quella
  // gia' presente: cosi' una scheda non ancora divisa non perde mai il suo nome.
  // Ordine scelto: NOME COGNOME (convenzione di 207 schede su 293).
  const nomeDaSalvare = (f) => {
    const n = (f.first_name || "").trim();
    const c = (f.last_name || "").trim();
    if (f.client_type === "private" && n && c) return (n + " " + c).toUpperCase();
    return (f.name || "").trim().toUpperCase();
  };

  // Validate all required fields
  const validateForm = () => {
    const errors = {};
    
    const nomeFinale = nomeDaSalvare(formData);
    if (!nomeFinale || nomeFinale.length < 2) {
      errors.name = formData.client_type === "private"
        ? "Servono il cognome e il nome del cliente"
        : "La ragione sociale deve avere almeno 2 caratteri";
    }

    if (
      formData.client_type === "private" &&
      !editingClient &&
      (!(formData.first_name || "").trim() || !(formData.last_name || "").trim())
    ) {
      errors.first_name = "Per un nuovo cliente privato servono sia il cognome sia il nome";
    }
    
    if (formData.email && !validators.email(formData.email)) {
      errors.email = "Email non valida";
    }
    
    if (formData.phone && !validators.phone(formData.phone)) {
      errors.phone = "Numero di telefono non valido";
    }
    
    if (formData.fiscal_code) {
      if (formData.client_type === "private" && !validators.fiscalCode(formData.fiscal_code)) {
        errors.fiscal_code = "Codice fiscale non valido";
      } else if (formData.client_type === "business" && !validators.vatNumber(formData.fiscal_code)) {
        errors.fiscal_code = "Partita IVA non valida";
      }
    }
    
    if (formData.cap && !validators.postalCode(formData.cap)) {
      errors.cap = "CAP non valido";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const policiesLoadedRef = useRef(false);
  const initialLoadDone = useRef(false);

  // Single fetch function for clients (search-aware)
  const fetchClients = async (page = 1, search = "") => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const requests = [
        axios.get(`/api/clients?page=${page}&limit=${ITEMS_PER_PAGE}${search ? `&search=${encodeURIComponent(search)}` : ''}`)
      ];
      // Load policies only once on first fetch
      if (!policiesLoadedRef.current) {
        requests.push(axios.get('/api/policies?all=true'));
      }
      
      const results = await Promise.all(requests);
      const clientsRes = results[0];
      
      if (clientsRes.data.items) {
        setClients(clientsRes.data.items);
        setTotalClients(clientsRes.data.total);
        setTotalPages(clientsRes.data.pages);
        setCurrentPage(clientsRes.data.page);
      } else {
        setClients(clientsRes.data);
      }
      
      if (results[1]) {
        setPolicies(results[1].data.items || results[1].data || []);
        policiesLoadedRef.current = true;
      }
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchClients(newPage, searchTerm);
    }
  };
  
  // Single useEffect for initial load + search with debounce
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchClients(1, searchTerm);
    }, searchTerm ? 400 : 0); // No delay on initial load
    
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Handle ?edit=clientId from navigation - fetch client directly if not in current page
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId) return;
    
    // First try from already loaded clients
    const clientToEdit = clients.find(c => c.client_id === editId);
    if (clientToEdit) {
      handleEdit(clientToEdit);
      setSearchParams({}, { replace: true });
      return;
    }
    
    // If clients are loaded but client not found, fetch directly
    if (initialLoadDone.current) {
      axios.get(`/api/clients/${editId}`).then(res => {
        handleEdit(res.data);
        setSearchParams({}, { replace: true });
      }).catch(() => {
        toast.error("Cliente non trovato");
        setSearchParams({}, { replace: true });
      });
    }
  }, [clients, searchParams]);

  // Count active filters
  useEffect(() => {
    let count = 0;
    if (advancedFilters.clientType) count++;
    if (advancedFilters.city) count++;
    if (advancedFilters.province) count++;
    if (advancedFilters.hasPolicies) count++;
    if (advancedFilters.fiscalCode) count++;
    if (advancedFilters.email) count++;
    if (advancedFilters.phone) count++;
    setActiveFiltersCount(count);
  }, [advancedFilters]);

  // Reset advanced filters
  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      clientType: "",
      city: "",
      province: "",
      hasPolicies: "",
      fiscalCode: "",
      email: "",
      phone: "",
    });
  };

  // Get unique cities and provinces
  const uniqueCities = [...new Set(clients.map(c => c.city))].filter(Boolean).sort();
  const uniqueProvinces = [...new Set(clients.map(c => c.province))].filter(Boolean).sort();

  // Check if client has active policies
  const clientHasPolicies = (clientId) => {
    return policies.some(p => p.client_id === clientId && !p.suspended);
  };

  const handleCityChange = (value) => {
    setFormData({ ...formData, city: value });
    
    // Cerca suggerimenti tra tutti i comuni (~7.900)
    if (value.length >= 2) {
      const search = value.toLowerCase();
      const suggestions = Object.keys(municipalities).filter(city => 
        city.toLowerCase().startsWith(search)
      ).slice(0, 8);
      setCitySuggestions(suggestions);
    } else {
      setCitySuggestions([]);
    }
  };

  const selectCity = (city) => {
    const data = municipalities[city];
    if (data) {
      setFormData({ 
        ...formData, 
        city: city,
        cap: data.c,
        province: data.p
      });
    }
    setCitySuggestions([]);
  };

  // Parse fiscal code to extract birth data
  const parseFiscalCode = async (cf) => {
    if (!cf || cf.length !== 16) return;
    
    setParsingCF(true);
    try {
      const response = await axios.get(`/api/clients/parse-fiscal-code/${cf}`);
      if (response.data.valid) {
        // Per stati esteri, mostra il nome dello stato
        let birthPlace = response.data.birth_place || "";
        let birthProvince = response.data.birth_place_province || "";
        
        // Se è estero, imposta la provincia come "EE" (Estero)
        if (response.data.estero) {
          birthProvince = "EE";
        }
        
        setFormData(prev => ({
          ...prev,
          birth_date: response.data.birth_date || prev.birth_date,
          birth_place: birthPlace || prev.birth_place,
          birth_place_province: birthProvince || prev.birth_place_province,
          gender: response.data.gender || prev.gender
        }));
        
        if (response.data.estero) {
          toast.success(`Dati estratti - Nato/a all'estero: ${birthPlace}`);
        } else {
          toast.success("Dati estratti dal codice fiscale");
        }
      }
    } catch (error) {
      // Silently fail - manual entry still possible
      // Debug removed
    } finally {
      setParsingCF(false);
    }
  };

  // Handle fiscal code change
  const handleFiscalCodeChange = (value) => {
    const upperValue = value.toUpperCase();
    setFormData({ ...formData, fiscal_code: upperValue });
    
    // Clear validation error while typing
    if (validationErrors.fiscal_code) {
      setValidationErrors(prev => ({ ...prev, fiscal_code: null }));
    }
    
    // Auto-parse when complete
    if (upperValue.length === 16 && formData.client_type === "private") {
      parseFiscalCode(upperValue);
    }
  };

  // Conferma un cliente creato da solo da un documento (gli toglie la spia).
  const confermaCliente = async (client) => {
    try {
      await axios.put(`/api/clients/${client.client_id}`, { da_confermare: false });
      toast.success(`${client.name} confermato`);
      fetchClients();
    } catch (err) {
      toast.error("Non confermato: " + (err?.response?.data?.detail || err.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      toast.error("Correggi gli errori nel form prima di salvare");
      return;
    }
    
    try {
      const submitData = { ...formData };
      // Clean up alias-related fields for submission
      delete submitData.email_alias;

      // v3.10: etichetta ricostruita da cognome+nome; il campo `name` non si svuota mai.
      submitData.name = nomeDaSalvare(formData);
      if (formData.client_type === "private") {
        submitData.first_name = (formData.first_name || "").trim().toUpperCase() || null;
        submitData.last_name = (formData.last_name || "").trim().toUpperCase() || null;
        if (submitData.first_name && submitData.last_name && formData.name_split_source !== "cf") {
          submitData.name_split_source = "manuale";
        }
      } else {
        submitData.first_name = null;
        submitData.last_name = null;
      }
      
      let createdClientId = null;
      
      if (editingClient) {
        await axios.put(`/api/clients/${editingClient.client_id}`, submitData);
        toast.success("Cliente aggiornato con successo");
      } else {
        const response = await axios.post('/api/clients', submitData);
        createdClientId = response.data?.client_id;
        toast.success("Cliente creato con successo");
      }
      
      // Auto-upload the document used for data extraction
      if (createdClientId && pendingFileRef.current) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append("file", pendingFileRef.current);
          await axios.post(
            `/api/documents/upload?entity_type=client&entity_id=${createdClientId}&storage=both&auto_rename=true`,
            formDataUpload,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          toast.success("Documento salvato nell'archivio del cliente");
        } catch (uploadErr) {
          console.error("Document auto-upload error:", uploadErr);
          toast.warning("Cliente creato ma il documento non è stato salvato nell'archivio. Puoi caricarlo manualmente.");
        }
        pendingFileRef.current = null;
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio del cliente");
    }
  };

  // Open delete confirmation dialog
  const confirmDelete = (client) => {
    setClientToDelete(client);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/clients/${clientToDelete.client_id}`);
      toast.success(`Cliente "${clientToDelete.name}" eliminato con successo`);
      setDeleteConfirmOpen(false);
      setClientToDelete(null);
      fetchClients();
    } catch (error) {
      toast.error("Errore nell'eliminazione del cliente");
    } finally {
      setDeleting(false);
    }
  };

  const handleRemoveAlias = async () => {
    if (!editingClient) return;
    setRemovingAlias(true);
    try {
      await axios.put(`/api/clients/${editingClient.client_id}`, { remove_alias: true });
      setFormData((prev) => ({ ...prev, email_alias: "", create_alias: false }));
      toast.success("Alias rimosso");
      setRemoveAliasOpen(false);
      fetchClients();
    } catch (error) {
      toast.error("Errore nella rimozione");
    } finally {
      setRemovingAlias(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      client_type: client.client_type || "private",
      name: client.name || "",
      first_name: client.first_name || "",
      last_name: client.last_name || "",
      name_split_source: client.name_split_source || null,
      email: client.email || "",
      phone: client.phone || "",
      fiscal_code: client.fiscal_code || "",
      birth_date: client.birth_date || "",
      birth_place: client.birth_place || "",
      birth_place_province: client.birth_place_province || "",
      gender: client.gender || "",
      pec: client.pec || "",
      sdi_code: client.sdi_code || "",
      referent: client.referent || "",
      address: client.address || "",
      city: client.city || "",
      cap: client.cap || "",
      province: client.province || "",
      notes: client.notes || "",
      create_alias: false,
      email_alias: client.email_alias || "",
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingClient(null);
    pendingFileRef.current = null;
    setFormData({
      client_type: "private",
      name: "",
      first_name: "",
      last_name: "",
      name_split_source: null,
      email: "",
      phone: "",
      fiscal_code: "",
      birth_date: "",
      birth_place: "",
      birth_place_province: "",
      gender: "",
      pec: "",
      sdi_code: "",
      referent: "",
      address: "",
      city: "",
      cap: "",
      province: "",
      notes: "",
      create_alias: false,
    });
    setValidationErrors({});
    setCitySuggestions([]);
    setTouched({});
  };

  const handleVoiceResult = async (transcript) => {
    try {
      const response = await axios.post('/api/ai/parse-client', { text: transcript });
      if (response.data.parsed_data) {
        const parsed = response.data.parsed_data;
        setFormData(prev => ({
          ...prev,
          name: parsed.name || prev.name,
          email: parsed.email || prev.email,
          phone: parsed.phone || prev.phone,
          fiscal_code: parsed.fiscal_code || prev.fiscal_code,
          address: parsed.address || prev.address,
          city: parsed.city || prev.city,
          cap: parsed.cap || prev.cap,
          province: parsed.province || prev.province,
          birth_date: parsed.birth_date || prev.birth_date,
          birth_place: parsed.birth_place || prev.birth_place,
          gender: parsed.gender || prev.gender,
          notes: parsed.notes || prev.notes
        }));
        toast.success("Dati estratti con successo dalla dettatura");
      }
    } catch (error) {
      toast.error("Errore nell'elaborazione della dettatura");
    }
  };

  const handleOCRResult = (parsed) => {
    setFormData(prev => ({
      ...prev,
      name: parsed.name || prev.name,
      email: parsed.email || prev.email,
      phone: parsed.phone || prev.phone,
      fiscal_code: parsed.fiscal_code || prev.fiscal_code,
      address: parsed.address || prev.address,
      city: parsed.city || prev.city,
      cap: parsed.cap || prev.cap,
      province: parsed.province || prev.province,
      birth_date: parsed.birth_date || prev.birth_date,
      birth_place: parsed.birth_place || prev.birth_place,
      gender: parsed.gender || prev.gender,
      client_type: parsed.client_type || prev.client_type,
      notes: parsed.notes || prev.notes
    }));
    setShowOCR(false);
  };

  // Filter clients - search is handled by backend, only apply advanced filters locally
  const filteredClients = clients.filter(client => {
    // Advanced filters only (search is done server-side)
    if (advancedFilters.clientType && client.client_type !== advancedFilters.clientType) return false;
    if (advancedFilters.city && client.city !== advancedFilters.city) return false;
    if (advancedFilters.province && client.province !== advancedFilters.province) return false;
    if (advancedFilters.hasPolicies === "yes" && !clientHasPolicies(client.client_id)) return false;
    if (advancedFilters.hasPolicies === "no" && clientHasPolicies(client.client_id)) return false;
    if (advancedFilters.fiscalCode && (!client.fiscal_code || !client.fiscal_code.toLowerCase().includes(advancedFilters.fiscalCode.toLowerCase()))) return false;
    if (advancedFilters.email && (!client.email || !client.email.toLowerCase().includes(advancedFilters.email.toLowerCase()))) return false;
    if (advancedFilters.phone && (!client.phone || !client.phone.includes(advancedFilters.phone))) return false;
    return true;
  });

  // Calculate client badges based on their policies
  const getClientBadges = (clientId) => {
    const clientPolicies = policies.filter(p => p.client_id === clientId && p.status !== "cancelled");
    const badges = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Calculate total annual premiums
    const totalPremiums = clientPolicies.reduce((sum, p) => sum + getPolicyPremiumTotal(p), 0);
    
    // Find earliest expiry date
    let earliestExpiry = null;
    let daysToExpiry = Infinity;
    
    clientPolicies.forEach(policy => {
      if (policy.expiry_date && !policy.renewed) {
        const expiryDate = new Date(policy.expiry_date);
        expiryDate.setHours(0, 0, 0, 0);
        const days = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        if (days < daysToExpiry) {
          daysToExpiry = days;
          earliestExpiry = policy.expiry_date;
        }
      }
      // Also check semiannual expiry
      if (policy.semiannual_expiry && policy.payment_frequency === "semiannual") {
        const semiDate = new Date(policy.semiannual_expiry);
        semiDate.setHours(0, 0, 0, 0);
        const days = Math.ceil((semiDate - today) / (1000 * 60 * 60 * 24));
        if (days < daysToExpiry) {
          daysToExpiry = days;
          earliestExpiry = policy.semiannual_expiry;
        }
      }
    });
    
    // Urgency badges (priority order)
    if (clientPolicies.length === 0) {
      badges.push({ type: "no-policies", label: "No polizze", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300", icon: "⚠️", priority: 3 });
    } else if (daysToExpiry <= 2 && daysToExpiry >= 0) {
      badges.push({ type: "urgent", label: `Scade ${daysToExpiry === 0 ? 'oggi' : daysToExpiry === 1 ? 'domani' : `tra ${daysToExpiry}gg`}`, color: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300", icon: "🔴", priority: 1 });
    } else if (daysToExpiry <= 15 && daysToExpiry > 2) {
      badges.push({ type: "attention", label: `Scade tra ${daysToExpiry}gg`, color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300", icon: "🟡", priority: 2 });
    } else if (daysToExpiry < 0) {
      badges.push({ type: "expired", label: `Scaduta`, color: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300", icon: "❌", priority: 1 });
    } else if (clientPolicies.length > 0) {
      badges.push({ type: "ok", label: "OK", color: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300", icon: "🟢", priority: 5 });
    }
    
    // High value badge
    if (totalPremiums >= 5000) {
      badges.push({ type: "high-value", label: `€${totalPremiums.toLocaleString('it-IT')}`, color: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300", icon: "💰", priority: 4 });
    }
    
    // Policy count badge
    if (clientPolicies.length > 1) {
      badges.push({ type: "policies", label: `${clientPolicies.length} polizze`, color: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300", icon: "📋", priority: 6 });
    }
    
    // Sort by priority
    return badges.sort((a, b) => a.priority - b.priority);
  };

  // Export filtered results
  const handleExportFiltered = async (format, columns, sortBy, sortDir, landscape) => {
    setExporting(true);
    try {
      const clientIds = filteredClients.map(c => c.client_id);
      const body = { client_ids: clientIds };
      if (columns) body.columns = columns;
      if (sortBy && sortBy !== "none") {
        body.sort_by = sortBy;
        body.sort_dir = sortDir || "asc";
      }
      if (landscape) body.landscape = true;
      
      const response = await axios.post(`/api/clients/export/${format}`, body, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = format === 'pdf' ? 'pdf' : 'xlsx';
      link.setAttribute('download', `clienti_filtrati.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${format.toUpperCase()} completato`);
      setExportDialogOpen(false);
    } catch (error) {
      toast.error("Errore nell'esportazione");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="clients-page-loading" className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <Skeleton className="h-10 w-28 mb-2" />
            <Skeleton className="h-4 w-56" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Skeleton className="h-10 w-28" />
            <Skeleton className="h-10 w-28" />
            <Skeleton className="h-10 w-36" />
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-5 w-5" />
                </div>
                <Skeleton className="h-8 w-12 mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search Skeleton */}
        <div className="flex flex-col md:flex-row gap-4">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Table Skeleton */}
        <Card>
          <CardContent className="p-0">
            <div className="space-y-0">
              <div className="p-4 border-b">
                <div className="flex gap-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-4 w-24" />
                  ))}
                </div>
              </div>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="p-4 border-b">
                  <div className="flex gap-4 items-center">
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-8 w-8" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="clients-page" className="space-y-6">
      <NameReviewDialog />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight">
            Clienti
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Gestisci il database dei tuoi clienti
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Export button */}
          <Button variant="outline" onClick={() => setExportDialogOpen(true)} data-testid="export-btn">
            <Download className="mr-2 h-4 w-4" />
            Esporta ({filteredClients.length})
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-client-btn" className="bg-primary hover:bg-primary/90">
              <Plus className="mr-2 h-4 w-4" />
              Nuovo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby={undefined} className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingClient ? "Modifica Cliente" : "Nuovo Cliente"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                {/* Document Dropzone + Voice */}
                <div className="space-y-2">
                  <DocumentDropzone
                    type="client"
                    onResult={handleOCRResult}
                    onClose={() => setShowOCR(false)}
                    onFileStored={(file) => { pendingFileRef.current = file; }}
                  />
                  <div className="flex items-center gap-2">
                    <VoiceInput onResult={handleVoiceResult} />
                    <span className="text-xs text-muted-foreground">
                      oppure dettatura vocale
                    </span>
                  </div>
                </div>

                {/* Tipo Cliente */}
                <div className="grid gap-2">
                  <Label>Tipo Cliente *</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.client_type === "private" ? "default" : "outline"}
                      className={`flex-1 ${formData.client_type === "private" ? "bg-primary" : ""}`}
                      onClick={() => setFormData({ ...formData, client_type: "private" })}
                    >
                      <User className="mr-2 h-4 w-4" />
                      Privato
                    </Button>
                    <Button
                      type="button"
                      variant={formData.client_type === "business" ? "default" : "outline"}
                      className={`flex-1 ${formData.client_type === "business" ? "bg-primary" : ""}`}
                      onClick={() => setFormData({ ...formData, client_type: "business" })}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      Azienda
                    </Button>
                  </div>
                </div>

                {/* Cognome e Nome (privati) / Ragione Sociale (aziende) */}
                {formData.client_type === "private" ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="grid gap-2">
                        <Label htmlFor="last_name" className={validationErrors.first_name ? "text-destructive" : ""}>
                          Cognome *
                        </Label>
                        <Input
                          id="last_name"
                          data-testid="client-lastname-input"
                          placeholder="ROSSI"
                          value={formData.last_name}
                          onChange={(e) => {
                            const upperValue = e.target.value.toUpperCase();
                            setFormData({ ...formData, last_name: upperValue });
                            if (validationErrors.first_name) {
                              setValidationErrors((prev) => ({ ...prev, first_name: null }));
                            }
                          }}
                          className={`uppercase ${validationErrors.first_name ? "border-destructive" : ""}`}
                          required
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="first_name" className={validationErrors.first_name ? "text-destructive" : ""}>
                          Nome *
                        </Label>
                        <Input
                          id="first_name"
                          data-testid="client-firstname-input"
                          placeholder="MARIO"
                          value={formData.first_name}
                          onChange={(e) => {
                            const upperValue = e.target.value.toUpperCase();
                            setFormData({ ...formData, first_name: upperValue });
                            if (validationErrors.first_name) {
                              setValidationErrors((prev) => ({ ...prev, first_name: null }));
                            }
                          }}
                          className={`uppercase ${validationErrors.first_name ? "border-destructive" : ""}`}
                          required
                        />
                      </div>
                    </div>

                    {(formData.first_name || "").trim() && (formData.last_name || "").trim() ? (
                      <p className="text-xs text-muted-foreground" data-testid="client-name-preview">
                        In elenco comparirà:{" "}
                        <span className="font-medium">
                          {(formData.first_name || "").trim()} {(formData.last_name || "").trim()}
                        </span>
                      </p>
                    ) : editingClient || (formData.name || "").trim() ? (
                      <div className="grid gap-2" data-testid="client-legacy-name-block">
                        <Label htmlFor="name" className={validationErrors.name ? "text-destructive" : ""}>
                          Nome completo (come compare in elenco) *
                        </Label>
                        <div className="relative">
                          <Input
                            id="name"
                            data-testid="client-name-input"
                            placeholder="MARIO ROSSI"
                            value={formData.name}
                            onChange={(e) => {
                              const upperValue = e.target.value.toUpperCase();
                              setFormData({ ...formData, name: upperValue });
                              if (validationErrors.name) validateField("name", upperValue);
                            }}
                            onBlur={() => handleFieldBlur("name")}
                            className={`uppercase ${validationErrors.name ? "border-destructive pr-10" : ""} ${touched.name && !validationErrors.name && formData.name ? "border-green-500 pr-10" : ""}`}
                            required
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {validationErrors.name && <AlertCircle className="h-4 w-4 text-destructive" />}
                            {touched.name && !validationErrors.name && formData.name && <CheckCircle className="h-4 w-4 text-green-500" />}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Questa scheda ha ancora il nome intero in un campo solo. Compila qui sopra
                          Cognome e Nome per dividerla: l'etichetta in elenco si aggiorna da sola.
                        </p>
                      </div>
                    ) : null}

                    {validationErrors.first_name && (
                      <p className="text-xs text-destructive">{validationErrors.first_name}</p>
                    )}
                  </>
                ) : (
                                <div className="grid gap-2">
                  <Label htmlFor="name" className={validationErrors.name ? "text-destructive" : ""}>
                    {formData.client_type === "private" ? "Nome e Cognome *" : "Ragione Sociale *"}
                  </Label>
                  <div className="relative">
                    <Input
                      id="name"
                      data-testid="client-name-input"
                      placeholder={formData.client_type === "private" ? "MARIO ROSSI" : "AZIENDA S.R.L."}
                      value={formData.name}
                      onChange={(e) => {
                        const upperValue = e.target.value.toUpperCase();
                        setFormData({ ...formData, name: upperValue });
                        if (validationErrors.name) validateField("name", upperValue);
                      }}
                      onBlur={() => handleFieldBlur("name")}
                      className={`uppercase ${validationErrors.name ? "border-destructive pr-10" : ""} ${touched.name && !validationErrors.name && formData.name ? "border-green-500 pr-10" : ""}`}
                      required
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {validationErrors.name && <AlertCircle className="h-4 w-4 text-destructive" />}
                      {touched.name && !validationErrors.name && formData.name && <CheckCircle className="h-4 w-4 text-green-500" />}
                    </div>
                  </div>
                  {validationErrors.name && (
                    <p className="text-xs text-destructive">{validationErrors.name}</p>
                  )}
                </div>)}

                {/* Codice Fiscale / Partita IVA */}
                <div className="grid gap-2">
                  <Label htmlFor="fiscal_code" className={validationErrors.fiscal_code ? "text-destructive" : ""}>
                    {formData.client_type === "private" ? "Codice Fiscale" : "Partita IVA"}
                  </Label>
                  <div className="relative">
                    <Input
                      id="fiscal_code"
                      data-testid="client-fiscal-input"
                      placeholder={formData.client_type === "private" ? "RSSMRA80A01H501U" : "01234567890"}
                      value={formData.fiscal_code}
                      onChange={(e) => handleFiscalCodeChange(e.target.value)}
                      onBlur={() => handleFieldBlur("fiscal_code")}
                      maxLength={formData.client_type === "private" ? 16 : 11}
                      className={`${validationErrors.fiscal_code ? "border-destructive" : ""} ${touched.fiscal_code && !validationErrors.fiscal_code && formData.fiscal_code ? "border-green-500" : ""}`}
                    />
                    {parsingCF && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                      </div>
                    )}
                    {!parsingCF && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {validationErrors.fiscal_code && <AlertCircle className="h-4 w-4 text-destructive" />}
                        {touched.fiscal_code && !validationErrors.fiscal_code && formData.fiscal_code && <CheckCircle className="h-4 w-4 text-green-500" />}
                      </div>
                    )}
                  </div>
                  {validationErrors.fiscal_code && (
                    <p className="text-xs text-destructive">{validationErrors.fiscal_code}</p>
                  )}
                  {!validationErrors.fiscal_code && formData.client_type === "private" && formData.fiscal_code.length === 16 && (
                    <p className="text-xs text-muted-foreground">
                      ✓ Dati di nascita estratti automaticamente
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="email" className={validationErrors.email ? "text-destructive" : ""}>Email</Label>
                    <div className="relative">
                      <Input
                        id="email"
                        type="email"
                        data-testid="client-email-input"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          if (validationErrors.email) validateField("email", e.target.value);
                        }}
                        onBlur={() => handleFieldBlur("email")}
                        className={`${validationErrors.email ? "border-destructive pr-10" : ""} ${touched.email && !validationErrors.email && formData.email ? "border-green-500 pr-10" : ""}`}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {validationErrors.email && <AlertCircle className="h-4 w-4 text-destructive" />}
                        {touched.email && !validationErrors.email && formData.email && <CheckCircle className="h-4 w-4 text-green-500" />}
                      </div>
                    </div>
                    {validationErrors.email && (
                      <p className="text-xs text-destructive">{validationErrors.email}</p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="phone" className={validationErrors.phone ? "text-destructive" : ""}>Telefono</Label>
                    <div className="relative">
                      <Input
                        id="phone"
                        data-testid="client-phone-input"
                        value={formData.phone}
                        onChange={(e) => {
                          setFormData({ ...formData, phone: e.target.value });
                          if (validationErrors.phone) validateField("phone", e.target.value);
                        }}
                        onBlur={() => handleFieldBlur("phone")}
                        className={`${validationErrors.phone ? "border-destructive pr-10" : ""} ${touched.phone && !validationErrors.phone && formData.phone ? "border-green-500 pr-10" : ""}`}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {validationErrors.phone && <AlertCircle className="h-4 w-4 text-destructive" />}
                        {touched.phone && !validationErrors.phone && formData.phone && <CheckCircle className="h-4 w-4 text-green-500" />}
                      </div>
                    </div>
                    {validationErrors.phone && (
                      <p className="text-xs text-destructive">{validationErrors.phone}</p>
                    )}
                  </div>
                </div>

                {/* Email Alias */}
                <div className="p-3 rounded-lg bg-muted/40 border space-y-2">
                  {editingClient && formData.email_alias ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Email Alias attivo</p>
                        <p className="text-xs text-primary font-mono">{formData.email_alias}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-destructive border-destructive hover:bg-destructive/10"
                        data-testid="remove-alias-btn"
                        onClick={() => setRemoveAliasOpen(true)}
                      >
                        Rimuovi Alias
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="create_alias"
                        data-testid="create-alias-checkbox"
                        checked={formData.create_alias}
                        onChange={(e) => setFormData({ ...formData, create_alias: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <div>
                        <Label htmlFor="create_alias" className="text-sm font-medium cursor-pointer">
                          Crea Email Alias
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Genera un indirizzo di inoltro automatico per questo cliente
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Dati di Nascita - solo per privati */}
                {formData.client_type === "private" && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="birth_date">Data di Nascita</Label>
                        <Input
                          id="birth_date"
                          type="date"
                          data-testid="client-birthdate-input"
                          value={formData.birth_date}
                          onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="birth_place">Luogo di Nascita</Label>
                        <Input
                          id="birth_place"
                          data-testid="client-birthplace-input"
                          placeholder="ROMA"
                          value={formData.birth_place}
                          onChange={(e) => setFormData({ ...formData, birth_place: e.target.value.toUpperCase() })}
                          className="uppercase"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="gender">Sesso</Label>
                        <Select
                          value={formData.gender}
                          onValueChange={(value) => setFormData({ ...formData, gender: value })}
                        >
                          <SelectTrigger id="gender" data-testid="client-gender-select">
                            <SelectValue placeholder="Seleziona" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="M">Maschio</SelectItem>
                            <SelectItem value="F">Femmina</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}

                {/* Dati Azienda - solo per business */}
                {formData.client_type === "business" && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="referent">Referente</Label>
                      <Input
                        id="referent"
                        data-testid="client-referent-input"
                        placeholder="NOME COGNOME"
                        value={formData.referent}
                        onChange={(e) => setFormData({ ...formData, referent: e.target.value.toUpperCase() })}
                        className="uppercase"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="pec">PEC</Label>
                      <Input
                        id="pec"
                        type="email"
                        data-testid="client-pec-input"
                        placeholder="azienda@pec.it"
                        value={formData.pec}
                        onChange={(e) => setFormData({ ...formData, pec: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="sdi_code">Codice SDI</Label>
                      <Input
                        id="sdi_code"
                        data-testid="client-sdi-input"
                        placeholder="0000000"
                        maxLength={7}
                        value={formData.sdi_code}
                        onChange={(e) => setFormData({ ...formData, sdi_code: e.target.value.toUpperCase() })}
                        className="uppercase"
                      />
                    </div>
                  </div>
                )}

                {/* Indirizzo */}
                <div className="grid gap-2">
                  <Label htmlFor="address">Indirizzo</Label>
                  <Input
                    id="address"
                    data-testid="client-address-input"
                    placeholder="VIA ROMA, 1"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value.toUpperCase() })}
                    className="uppercase"
                  />
                </div>

                {/* Città, CAP, Provincia */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="grid gap-2 relative">
                    <Label htmlFor="city">Città</Label>
                    <Input
                      id="city"
                      data-testid="client-city-input"
                      placeholder="ROMA"
                      value={formData.city}
                      onChange={(e) => handleCityChange(e.target.value.toUpperCase())}
                      autoComplete="off"
                      className="uppercase"
                    />
                    {citySuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 z-50 bg-card border border-border rounded-md shadow-lg mt-1">
                        {citySuggestions.map((city) => (
                          <div
                            key={city}
                            className="px-3 py-2 cursor-pointer hover:bg-accent text-sm"
                            onClick={() => selectCity(city)}
                          >
                            {city} ({municipalities[city]?.c || ""} - {municipalities[city]?.p || ""})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="cap">CAP</Label>
                    <Input
                      id="cap"
                      data-testid="client-cap-input"
                      placeholder="00100"
                      value={formData.cap}
                      onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="province">Prov.</Label>
                    <Input
                      id="province"
                      data-testid="client-province-input"
                      placeholder="RM"
                      maxLength={2}
                      value={formData.province}
                      onChange={(e) => setFormData({ ...formData, province: e.target.value.toUpperCase() })}
                      className="uppercase"
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="notes">Note</Label>
                  <Textarea
                    id="notes"
                    data-testid="client-notes-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="save-client-btn" className="bg-primary hover:bg-primary/90">
                  {editingClient ? "Aggiorna" : "Salva"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search and Advanced Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="search-clients-input"
            placeholder="Cerca per nome, email, telefono, CF..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Sheet open={advancedFiltersOpen} onOpenChange={setAdvancedFiltersOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="relative">
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Filtri
              {activeFiltersCount > 0 && (
                <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs bg-primary">
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-[400px] sm:w-[450px] overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-heading flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filtri Avanzati Clienti
              </SheetTitle>
            </SheetHeader>
            
            <div className="space-y-6 py-6">
              {/* Client Type */}
              <div className="space-y-2">
                <Label>Tipo Cliente</Label>
                <Select
                  value={advancedFilters.clientType}
                  onValueChange={(value) => setAdvancedFilters(prev => ({ ...prev, clientType: value === "all" ? "" : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tutti i tipi" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i tipi</SelectItem>
                    <SelectItem value="private">Privato</SelectItem>
                    <SelectItem value="business">Azienda</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label>Città</Label>
                <Select
                  value={advancedFilters.city}
                  onValueChange={(value) => setAdvancedFilters(prev => ({ ...prev, city: value === "all" ? "" : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tutte le città" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le città</SelectItem>
                    {uniqueCities.map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Province */}
              <div className="space-y-2">
                <Label>Provincia</Label>
                <Select
                  value={advancedFilters.province}
                  onValueChange={(value) => setAdvancedFilters(prev => ({ ...prev, province: value === "all" ? "" : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tutte le province" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le province</SelectItem>
                    {uniqueProvinces.map(prov => (
                      <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Has Policies */}
              <div className="space-y-2">
                <Label>Polizze Attive</Label>
                <Select
                  value={advancedFilters.hasPolicies}
                  onValueChange={(value) => setAdvancedFilters(prev => ({ ...prev, hasPolicies: value === "all" ? "" : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tutti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="yes">Con polizze attive</SelectItem>
                    <SelectItem value="no">Senza polizze attive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Fiscal Code */}
              <div className="space-y-2">
                <Label>Codice Fiscale / P.IVA</Label>
                <Input
                  placeholder="Cerca CF o P.IVA..."
                  value={advancedFilters.fiscalCode}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, fiscalCode: e.target.value.toUpperCase() }))}
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  placeholder="Cerca email..."
                  value={advancedFilters.email}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input
                  placeholder="Cerca telefono..."
                  value={advancedFilters.phone}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
            </div>

            <SheetFooter className="flex gap-2">
              <Button variant="outline" onClick={resetAdvancedFilters} className="flex-1">
                <X className="h-4 w-4 mr-2" />
                Resetta
              </Button>
              <Button onClick={() => setAdvancedFiltersOpen(false)} className="flex-1">
                <CheckCircle className="h-4 w-4 mr-2" />
                Applica ({filteredClients.length})
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      {/* Active filters summary */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>{activeFiltersCount} filtri attivi</span>
          <span>•</span>
          <span className="font-medium text-foreground">{filteredClients.length} di {totalClients} risultati</span>
          <Button variant="ghost" size="sm" onClick={resetAdvancedFilters} className="h-6 px-2 text-xs">
            Resetta filtri
          </Button>
        </div>
      )}

      {/* Clients Table - Desktop */}
      <Card className="bg-card border-border hidden md:block">
        <CardContent className="p-0">
          {loadError && filteredClients.length === 0 ? (
            <div className="text-center py-12 space-y-3">
              <AlertTriangle className="h-8 w-8 mx-auto text-amber-500" />
              <p className="font-medium text-foreground">Non riesco a caricare i clienti</p>
              <p className="text-sm text-muted-foreground">
                I dati non sono andati persi: e' il collegamento che non ha risposto.
              </p>
              <Button variant="outline" size="sm" onClick={() => fetchClients(currentPage, searchTerm)}>
                Riprova
              </Button>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {searchTerm ? "Nessun cliente trovato" : "Nessun cliente presente. Crea il primo!"}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <SortableHeader label="Tipo" sortKey="client_type" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Nome / Ragione Sociale" sortKey="name" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                  </TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>
                    <SortableHeader label="CF / P.IVA" sortKey="fiscal_code" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Citta" sortKey="city" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Telefono" sortKey="phone" currentSort={tableSort.key} currentDir={tableSort.dir} onSort={(key, dir) => setTableSort({ key, dir })} />
                  </TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortData(filteredClients, tableSort.key, tableSort.dir).map((client) => (
                  <TableRow 
                    key={client.client_id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/clients/${client.client_id}`)}
                    data-testid={`client-row-${client.client_id}`}
                  >
                    <TableCell>
                      {client.client_type === "business" ? (
                        <Building2 className="h-4 w-4 text-muted-foreground" title="Azienda" />
                      ) : (
                        <User className="h-4 w-4 text-muted-foreground" title="Privato" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-primary">{client.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {client.da_confermare ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                            title="Creato da solo da un documento: controllalo e conferma"
                            data-testid={`client-da-confermare-${client.client_id}`}
                          >
                            🆕 <span className="hidden lg:inline">da confermare</span>
                          </span>
                        ) : null}
                        {getClientBadges(client.client_id).slice(0, 2).map((badge, idx) => (
                          <span
                            key={idx}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}
                            title={badge.label}
                          >
                            <span>{badge.icon}</span>
                            <span className="hidden lg:inline">{badge.label}</span>
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{client.fiscal_code || "-"}</TableCell>
                    <TableCell>{client.city || "-"}</TableCell>
                    <TableCell>{client.phone || "-"}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        {client.da_confermare ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-amber-700"
                            title="Conferma il cliente creato da solo da un documento"
                            data-testid={`confirm-client-${client.client_id}`}
                            onClick={() => confermaCliente(client)}
                          >
                            Conferma
                          </Button>
                        ) : null}
                        <Button
                          variant="outline"
                          size="icon"
                          title="Documenti"
                          onClick={() => {
                            setSelectedClientForDocs(client);
                            setDocumentsDialogOpen(true);
                          }}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          data-testid={`edit-client-${client.client_id}`}
                          onClick={() => handleEdit(client)}
                         aria-label="Modifica il cliente">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          data-testid={`delete-client-${client.client_id}`}
                          onClick={() => confirmDelete(client)}
                          className="text-destructive hover:text-destructive"
                         aria-label="Elimina il cliente">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Server-side Pagination */}
          {totalPages > 1 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalClients}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Clients Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {loadError && filteredClients.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12 space-y-3">
              <AlertTriangle className="h-8 w-8 mx-auto text-amber-500" />
              <p className="font-medium text-foreground">Non riesco a caricare i clienti</p>
              <p className="text-sm text-muted-foreground">
                I dati non sono andati persi: e' il collegamento che non ha risposto.
              </p>
              <Button variant="outline" size="sm" onClick={() => fetchClients(currentPage, searchTerm)}>
                Riprova
              </Button>
            </CardContent>
          </Card>
        ) : filteredClients.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <p className="text-muted-foreground">
                {searchTerm ? "Nessun cliente trovato" : "Nessun cliente presente. Crea il primo!"}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredClients.map((client) => (
            <Card 
              key={client.client_id} 
              className="bg-card border-border cursor-pointer hover:shadow-md hover:border-primary/30 transition-all"
              onClick={() => navigate(`/clients/${client.client_id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-muted rounded-sm">
                      {client.client_type === "business" ? (
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <User className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">
                        {client.name}
                      </h3>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {getClientBadges(client.client_id).slice(0, 3).map((badge, idx) => (
                          <span
                            key={idx}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}
                          >
                            <span>{badge.icon}</span>
                            <span>{badge.label}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        setSelectedClientForDocs(client);
                        setDocumentsDialogOpen(true);
                      }}
                     aria-label="Documenti del cliente">
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(client)}
                     aria-label="Modifica il cliente">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => confirmDelete(client)}
                      className="text-destructive hover:text-destructive"
                     aria-label="Elimina il cliente">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-2 text-sm">
                  {client.fiscal_code && (
                    <div>
                      <span className="text-muted-foreground">CF/P.IVA: </span>
                      <span className="text-foreground">{client.fiscal_code}</span>
                    </div>
                  )}
                  {client.city && (
                    <div>
                      <span className="text-muted-foreground">Città: </span>
                      <span className="text-foreground">{client.city}</span>
                    </div>
                  )}
                  {client.phone && (
                    <div>
                      <span className="text-muted-foreground">Tel: </span>
                      <span className="text-foreground">{client.phone}</span>
                    </div>
                  )}
                  {client.email && (
                    <div>
                      <span className="text-muted-foreground">Email: </span>
                      <span className="text-foreground truncate">{client.email}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
        
        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <DataPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalClients}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      {/* Documents Dialog */}
      {selectedClientForDocs && (
        <DocumentsDialog
          open={documentsDialogOpen}
          onOpenChange={setDocumentsDialogOpen}
          entityType="client"
          entityId={selectedClientForDocs.client_id}
          entityName={selectedClientForDocs.name}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={removeAliasOpen}
        onOpenChange={setRemoveAliasOpen}
        title="Rimuovere l'alias email?"
        description={
          `Stai per rimuovere l'alias "${formData.email_alias || ""}". ` +
          `I messaggi inviati a quell'indirizzo non arriveranno più. ` +
          `Puoi ricrearlo quando vuoi.`
        }
        confirmText="Rimuovi alias"
        cancelText="Annulla"
        onConfirm={handleRemoveAlias}
        variant="danger"
        loading={removingAlias}
      />

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        itemName={clientToDelete?.name || "questo cliente"}
        onConfirm={handleDelete}
        loading={deleting}
        description={
          `Stai per eliminare "${clientToDelete?.name || "questo cliente"}" e, INSIEME a lui, ` +
          `TUTTE le sue polizze e TUTTI i suoi documenti. L'operazione non è annullabile dall'app.`
        }
        requireText="ELIMINA"
        details={
          <span>
            Se non sei sicuro, annulla: nessun dato viene toccato.
          </span>
        }
      />
      <ExportColumnsDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        exportType="clients"
        onExport={handleExportFiltered}
        exporting={exporting}
      />
    </div>
  );
}
